// Code generated by spellbook: DO NOT EDIT.
import { z } from "zod";

export type RequestReason = "Transport" | "Encode" | "Abort" | "StreamRead";

/**
 * Error thrown for request-related issues (e.g., network, validation, abort).
 */
export class RequestError extends Error {
    constructor(
        public readonly request: Request,
        public readonly reason: RequestReason,
        public readonly cause?: unknown,
        public readonly description?: string,
        public readonly extra?: Record<string, unknown>,
    ) {
        super();
        this.name = "RequestError";
    }
    get methodAndUrl() {
        return this.request.method + " " + this.request.url;
    }
    get message() {
        return this.description
            ? this.reason + ": " + this.description + " (" + this.methodAndUrl + ")"
            : this.reason + " error (" + this.methodAndUrl + ")";
    }
}

export type ResponseReason = "StatusCode" | "Decode" | "EmptyBody";

export type IdempotencyMode = "enabled" | "disabled";

export type RouteIdempotencyPolicy = {
    mode: IdempotencyMode;
    generateKey: boolean;
    required: boolean;
    requireIdentity: boolean;
};

/** Registration point for generated-client request context augmentation. */
export interface SpellbookRegister {}

/** Context supplied by the host application for one request. */
export type RequestContext = keyof SpellbookRegister extends never
    ? Readonly<Record<string, unknown>>
    : SpellbookRegister extends { requestContext?: infer T }
      ? NonNullable<T>
      : Readonly<Record<string, unknown>>;

/** Header values accepted by individual calls and scope resolvers. */
export type HeaderOverrides = HeadersInit | Readonly<Record<string, string | null>>;

/** Metadata that travels with a request without entering its URL or body. */
export type RequestMeta = {
    requestContext?: RequestContext;
    headers?: HeaderOverrides;
    signal?: AbortSignal;
    idempotencyKey?: string;
};

/** Stable identity parts that distinguish data belonging to different scopes. */
export type CacheIdentity = readonly (string | number | boolean | null)[];

/** Operation metadata passed to the host's scope resolver. */
export type ScopeOperation = {
    method: string;
    pathTemplate: string;
    requiredScopes?: readonly string[];
};

/** Result returned by the host's synchronous scope resolver. */
export type ScopeContextResolution = {
    availableScopes: readonly string[];
    cacheIdentity: CacheIdentity;
    headers?: HeaderOverrides;
} & ({ status: "ready" } | { status: "blocked"; reason: string });

/** Callback that resolves request context, authorization, headers, and cache identity. */
export type ResolveScopeContext = (input: {
    operation: ScopeOperation;
    requestContext?: RequestContext;
}) => ScopeContextResolution;

/** Scope state captured by a query or logical mutation operation. */
export type ScopeContextBinding = {
    usesResolver: boolean;
    status: "ready" | "blocked";
    cacheIdentity: CacheIdentity;
    reason?: string;
    missingScopes: readonly string[];
};

/** Eligibility returned by the exported scope check helper. */
export type ScopeEligibility = Omit<ScopeContextBinding, "status" | "reason"> &
    (
        | { allowed: true; status: "ready"; missingScopes: readonly [] }
        | { allowed: false; status: "blocked"; reason: string; missingScopes: readonly string[] }
    );

/** Error raised when an operation is blocked by its current scope context. */
export class ScopeUnavailableError extends Error {
    readonly reason: string;

    constructor(
        public readonly operation: ScopeOperation,
        public readonly eligibility: Extract<ScopeEligibility, { allowed: false }>,
    ) {
        super(eligibility.reason);
        this.reason = eligibility.reason;
        this.name = "ScopeUnavailableError";
    }
}

/** Error raised when a captured operation is executed under a different scope. */
export class ScopeContextChangedError extends Error {
    constructor() {
        super("The request scope changed after this operation was created");
        this.name = "ScopeContextChangedError";
    }
}

/** Error categories emitted by the generated Spellbook client. */
export type SpellbookError =
    | RequestError
    | ResponseError
    | ScopeUnavailableError
    | ScopeContextChangedError;

export type SpellbookErrorSource = "call" | "query" | "mutation";

/** Context describing the source operation for one Spellbook error. */
export type SpellbookErrorContext = {
    source: SpellbookErrorSource;
    operation: ScopeOperation;
};

/**
 * Error thrown for response-related issues (e.g., status code, decode, empty body).
 */
export class ResponseError extends Error {
    constructor(
        public readonly request: Request,
        public readonly response: Response,
        public readonly reason: ResponseReason,
        public readonly cause?: unknown,
        public readonly description?: string,
        public readonly extra?: Record<string, unknown>,
        public readonly serverMsg?: string,
        public readonly metadata?: Record<string, unknown>,
        public readonly bodyParsed: boolean = false,
        public readonly scopeBinding: ScopeContextBinding = {
            usesResolver: false,
            status: "ready",
            cacheIdentity: [],
            missingScopes: [],
        },
    ) {
        super();
        this.name = "ResponseError";
    }
    get methodAndUrl() {
        return this.request.method + " " + this.request.url;
    }
    get message() {
        const info = this.response.status + " " + this.methodAndUrl;
        return this.description
            ? this.reason + ": " + this.description + " (" + info + ")"
            : this.reason + " error (" + info + ")";
    }
}

export type SpellbookConfig = {
    baseUrl: string;
    headers?: HeadersInit | (() => HeadersInit);
    fetch?: (request: Request) => Promise<Response>;
    resolveScopeContext?: ResolveScopeContext;
    idempotencyKeyFactory?: () => string;
    /**
     * Handles request, response, and scope errors, including invalid JSON.
     * Response schema mismatches are logged to the console instead, with the
     * existing response fallback or stream-item skipping behavior preserved.
     */
    onError?: (err: SpellbookError, context: SpellbookErrorContext) => void;
};

type ErrorNotificationOptions = {
    /** Can be a predicate when a managed integration owns final error reporting. */
    notifyOnError?: boolean | (() => boolean);
    errorContext?: SpellbookErrorContext;
};

type ScopeErrorNotificationOptions = ErrorNotificationOptions & {
    errorContext: SpellbookErrorContext;
};

const scopeErrorNotifications = new WeakMap<ScopeContextBinding, ScopeErrorNotificationOptions>();

let config: SpellbookConfig = {
    baseUrl: "",
};

/**
 * Set global configuration for spellbook API calls (baseUrl, headers, error handler).
 */
export function setSpellbookConfig(newConfig: Partial<SpellbookConfig>) {
    config = { ...config, ...newConfig };
}

/**
 * Get the current global configuration for spellbook API calls.
 */
export function getSpellbookConfig(): SpellbookConfig {
    return config;
}

/**
 * Associates one execution-specific error policy with a cloned scope binding.
 * Adapters can pass the returned binding to createCall or createStream to keep
 * the managed operation's notification context without mutating shared state.
 */
export function withScopeErrorNotification(
    expectedScope: ScopeContextBinding,
    options: ScopeErrorNotificationOptions,
): ScopeContextBinding {
    const scopedBinding = { ...expectedScope };
    scopeErrorNotifications.set(scopedBinding, {
        ...options,
        errorContext: { ...options.errorContext },
    });
    return scopedBinding;
}

/** Reports a Spellbook error through the configured host callback. */
export function notifySpellbookError(error: SpellbookError, context: SpellbookErrorContext): void {
    config.onError?.(error, context);
}

/** Resolves and checks the current scope for one generated operation. */
export function resolveScopeBinding(
    operation: ScopeOperation,
    requestContext?: RequestContext,
): { binding: ScopeContextBinding; headers?: HeaderOverrides } {
    const resolver = getSpellbookConfig().resolveScopeContext;
    if (!resolver) {
        return {
            binding: { usesResolver: false, status: "ready", cacheIdentity: [], missingScopes: [] },
        };
    }

    const result = resolver({ operation, requestContext });
    const cacheIdentity = [...result.cacheIdentity];
    const missingScopes = (operation.requiredScopes ?? []).filter(
        (scope) => !result.availableScopes.includes(scope),
    );
    if (result.status === "blocked") {
        return {
            binding: {
                usesResolver: true,
                status: "blocked",
                cacheIdentity,
                reason: result.reason,
                missingScopes,
            },
            headers: result.headers,
        };
    }

    if (missingScopes.length > 0) {
        return {
            binding: {
                usesResolver: true,
                status: "blocked",
                cacheIdentity,
                reason: `Required API scopes are unavailable: ${missingScopes.join(", ")}`,
                missingScopes,
            },
            headers: result.headers,
        };
    }

    return {
        binding: { usesResolver: true, status: "ready", cacheIdentity, missingScopes: [] },
        headers: result.headers,
    };
}

/** Checks current operation eligibility without exposing resolved headers. */
export function checkScopeEligibility(
    operation: ScopeOperation,
    context?: Pick<RequestMeta, "requestContext">,
): ScopeEligibility {
    const { binding } = resolveScopeBinding(operation, context?.requestContext);
    return scopeEligibilityFromBinding(binding);
}

/** Converts a captured scope binding into the approved eligibility union. */
export function scopeEligibilityFromBinding(binding: ScopeContextBinding): ScopeEligibility {
    if (binding.status === "ready") {
        return {
            usesResolver: binding.usesResolver,
            cacheIdentity: binding.cacheIdentity,
            status: "ready",
            allowed: true,
            missingScopes: [],
        };
    }
    return {
        usesResolver: binding.usesResolver,
        cacheIdentity: binding.cacheIdentity,
        status: "blocked",
        allowed: false,
        reason: binding.reason ?? "The request scope is unavailable",
        missingScopes: binding.missingScopes,
    };
}

function sameScopeBinding(left: ScopeContextBinding, right: ScopeContextBinding): boolean {
    return (
        left.usesResolver === right.usesResolver &&
        left.status === right.status &&
        JSON.stringify(left.cacheIdentity) === JSON.stringify(right.cacheIdentity)
    );
}

function assertScopeBinding(
    operation: ScopeOperation,
    requestContext: RequestContext | undefined,
    expected?: ScopeContextBinding,
): { binding: ScopeContextBinding; headers?: HeaderOverrides } {
    const current = resolveScopeBinding(operation, requestContext);
    if (expected && !sameScopeBinding(expected, current.binding)) {
        throw new ScopeContextChangedError();
    }
    if (current.binding.status === "blocked") {
        throw new ScopeUnavailableError(
            operation,
            scopeEligibilityFromBinding(current.binding) as Extract<
                ScopeEligibility,
                { allowed: false }
            >,
        );
    }
    return current;
}

function mergeRequestHeaders(
    globalHeaders: HeadersInit | undefined,
    inputHeaders: HeaderOverrides | undefined,
    scopeHeaders: HeaderOverrides | undefined,
): Headers {
    const headers = new Headers(globalHeaders);
    applyHeaderOverrides(headers, inputHeaders);
    applyHeaderOverrides(headers, scopeHeaders);
    return headers;
}

function shouldNotifyError(options: ErrorNotificationOptions | undefined): boolean {
    const notifyOnError = options?.notifyOnError;
    return typeof notifyOnError === "function" ? notifyOnError() : (notifyOnError ?? true);
}

function notifyOperationError(
    error: SpellbookError,
    operation: ScopeOperation,
    options: (ErrorNotificationOptions & { expectedScope?: ScopeContextBinding }) | undefined,
): void {
    const inherited = options?.expectedScope
        ? scopeErrorNotifications.get(options.expectedScope)
        : undefined;
    const notificationOptions = {
        notifyOnError: options?.notifyOnError ?? inherited?.notifyOnError,
        errorContext: options?.errorContext ?? inherited?.errorContext,
    };
    if (shouldNotifyError(notificationOptions)) {
        notifySpellbookError(
            error,
            notificationOptions.errorContext ?? { source: "call", operation },
        );
    }
}

/** Identifies errors emitted by this SDK and narrows unknown catch values. */
export function isSpellbookError(error: unknown): error is SpellbookError {
    return (
        error instanceof RequestError ||
        error instanceof ResponseError ||
        error instanceof ScopeUnavailableError ||
        error instanceof ScopeContextChangedError
    );
}

function applyHeaderOverrides(headers: Headers, overrides: HeaderOverrides | undefined): void {
    if (!overrides) {
        return;
    }

    if (overrides instanceof Headers || Array.isArray(overrides)) {
        new Headers(overrides as HeadersInit).forEach((value, key) => headers.set(key, value));
        return;
    }

    for (const [key, value] of Object.entries(overrides)) {
        if (value === null) {
            headers.delete(key);
        } else {
            headers.set(key, value);
        }
    }
}

/** Creates a new idempotency key for one logical mutation operation. */
export function createIdempotencyKey(): string {
    if (config.idempotencyKeyFactory) {
        return config.idempotencyKeyFactory();
    }
    if (typeof globalThis.crypto?.randomUUID === "function") {
        return globalThis.crypto.randomUUID();
    }
    throw new Error("No secure idempotency key generator is available");
}

function isMutationMethod(method: string): boolean {
    return ["POST", "PUT", "PATCH", "DELETE"].includes(method.toUpperCase());
}

/**
 * Build a URL path from a template and params/query objects.
 */
export function buildPath<P = undefined, Q = undefined>(
    pathTemplate: string,
): undefined extends P
    ? Q extends undefined
        ? () => string
        : (opts: { query: Q }) => string
    : Q extends undefined
      ? (opts: { params: P }) => string
      : (opts: { params: P; query: Q }) => string;
export function buildPath(pathTemplate: string) {
    return (opts: any = {}) => {
        let url = pathTemplate.replace(/\{([^}]+)\}/g, (_, key) =>
            encodeURIComponent(opts.params[key]),
        );
        if (opts.query) {
            // Build query parameters with dotted object keys, repeated arrays, and ISO dates.
            const searchParams = new URLSearchParams();
            const appendQueryParam = (key: string, value: unknown) => {
                if (value === null || value === undefined) {
                    return;
                }
                if (Array.isArray(value)) {
                    value.forEach((v) => {
                        if (v === null || v === undefined) {
                            return;
                        }
                        searchParams.append(key, v instanceof Date ? v.toISOString() : String(v));
                    });
                } else if (typeof value === "object" && !(value instanceof Date)) {
                    Object.entries(value).forEach(([nestedKey, nestedValue]) => {
                        appendQueryParam(`${key}.${nestedKey}`, nestedValue);
                    });
                } else {
                    searchParams.append(
                        key,
                        value instanceof Date ? value.toISOString() : String(value),
                    );
                }
            };
            Object.entries(opts.query).forEach(([key, value]) => appendQueryParam(key, value));
            const qs = searchParams.toString();
            url += "?" + qs;
        }
        return url;
    };
}

/**
 * Recursively converts date strings to Date objects in the data structure.
 * Checks fields and nested fields for potential date strings and converts valid date strings to Date objects.
 */
function coerceDates(data: unknown): unknown {
    if (data === null || data === undefined) {
        return data;
    }

    if (typeof data === "string") {
        // Check if the string looks like an ISO date string
        // Matches formats like:
        // - 2024-01-01T12:34:56.789Z
        // - 2024-01-01T12:34:56Z
        // - 2024-01-01T12:34:56+00:00
        // - 2024-01-01
        // - 2024-01-01T12:34:56
        const isoDatePattern =
            /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:\d{2})?)?$/;
        if (isoDatePattern.test(data)) {
            const date = new Date(data);
            if (!isNaN(date.getTime())) {
                if (data.length >= 10 && data.length <= 35) {
                    return date;
                }
            }
        }
        return data;
    }

    if (Array.isArray(data)) {
        return data.map((item) => coerceDates(item));
    }

    if (typeof data === "object") {
        const result: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
            result[key] = coerceDates(value);
        }
        return result;
    }

    return data;
}

/**
 * Creates a type-safe API call function with validation and error handling.
 * Accepts Zod schemas for params, query, request, and response, with an optional fetcher.
 */
export function createCall<P = undefined, Q = undefined, Req = undefined, Res = undefined>(
    ingredients: {
        p?: z.ZodType<P>;
        q?: z.ZodType<Q>;
        req?: z.ZodType<Req>;
        res?: z.ZodType<Res>;
        pathTemplate: string;
        method: string;
        idempotency?: RouteIdempotencyPolicy;
        requiredScopes?: readonly string[];
    },
    fetcher?: (request: Request) => Promise<Response>,
    options?: {
        baseUrl?: string;
        getRequestErrorExtra?: (req: Req) => Record<string, unknown>;
        expectedScope?: ScopeContextBinding;
    } & ErrorNotificationOptions,
) {
    type Arg<P, Q, Req> = [P, Q, Req] extends [undefined, undefined, undefined]
        ? RequestMeta | undefined
        : (P extends undefined ? {} : { params: P }) &
              (Q extends undefined ? {} : { query: Q }) &
              (Req extends undefined ? {} : { req: Req }) &
              RequestMeta;
    return async function (args: Arg<P, Q, Req>): Promise<Res extends undefined ? void : Res> {
        const config = getSpellbookConfig();
        let scope: ReturnType<typeof assertScopeBinding>;
        try {
            scope = assertScopeBinding(ingredients, args?.requestContext, options?.expectedScope);
        } catch (error) {
            if (isSpellbookError(error)) {
                notifyOperationError(error, ingredients, options);
            }
            throw error;
        }
        const scopeBinding = scope.binding;
        const scopeHeaders = scope.headers;

        // Prepare the request
        const requestPath = buildPath<P, Q>(ingredients.pathTemplate)({
            ...(args && "params" in args ? { params: args.params } : {}),
            ...(args && "query" in args ? { query: args.query } : {}),
        } as { params: P; query: Q });
        const baseUrl = options?.baseUrl ?? config.baseUrl;
        if (!baseUrl) {
            throw new Error(
                "No baseUrl provided. Please set a baseUrl in the config or options before making API calls.",
            );
        }
        const url = new URL(requestPath, baseUrl).toString();

        // Support both static headers and dynamic header functions
        const headersToUse =
            typeof config.headers === "function" ? config.headers() : config.headers;
        const headers = mergeRequestHeaders(headersToUse, args?.headers, scopeHeaders);
        const explicitIdempotencyKey = args?.idempotencyKey;
        if (explicitIdempotencyKey) {
            headers.set("Idempotency-Key", explicitIdempotencyKey);
        } else if (ingredients.idempotency?.generateKey && isMutationMethod(ingredients.method)) {
            headers.set("Idempotency-Key", createIdempotencyKey());
        }

        const init: RequestInit = {
            method: ingredients.method,
            headers,
            signal: args?.signal,
            ...(args && "req" in args ? { body: JSON.stringify(args.req) } : {}),
        };

        const request = new Request(url, init);

        const requestErrorExtra = {
            ...(args && "params" in args ? { params: args.params } : {}),
            ...(args && "query" in args ? { query: args.query } : {}),
            ...(args && "req" in args && options?.getRequestErrorExtra
                ? options.getRequestErrorExtra(args.req)
                : {}),
        };

        // Validate Path params
        if (ingredients.p && args && "params" in args) {
            const pResult = ingredients.p.safeParse(args.params);
            if (!pResult.success) {
                const error = new RequestError(
                    request,
                    "Encode",
                    pResult.error,
                    "Path parameters failed validation:" + z.prettifyError(pResult.error),
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        // Validate Query params
        if (ingredients.q && args && "query" in args) {
            const qResult = ingredients.q.safeParse(args.query);
            if (!qResult.success) {
                const error = new RequestError(
                    request,
                    "Encode",
                    qResult.error,
                    "Query parameters failed validation:" + z.prettifyError(qResult.error),
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        // Validate Request body
        if (ingredients.req && args && "req" in args) {
            const reqResult = ingredients.req.safeParse(args.req);
            if (!reqResult.success) {
                const error = new RequestError(
                    request,
                    "Encode",
                    reqResult.error,
                    "Request body failed validation:" + z.prettifyError(reqResult.error),
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        // Make the request
        let response: Response;
        const effectiveFetch =
            fetcher ??
            getSpellbookConfig().fetch ??
            ((nextRequest: Request) => globalThis.fetch(nextRequest));
        try {
            response = await effectiveFetch(request);
        } catch (err) {
            // Handle AbortError
            if (err instanceof DOMException && err.name === "AbortError") {
                const error = new RequestError(
                    request,
                    "Abort",
                    err,
                    "Request cancelled by an Abort Signal",
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
            // Handle TransportError
            const error = new RequestError(
                request,
                "Transport",
                err,
                "Network request failed",
                requestErrorExtra,
            );
            notifyOperationError(error, ingredients, options);
            throw error;
        }

        // Handle StatusCodeError
        if (!response.ok) {
            let serverMsg: string | undefined;
            let metadata: Record<string, unknown> | undefined;
            let bodyParsed = false;
            try {
                const body = await response.clone().json();
                bodyParsed = true;
                metadata = body.metadata;
                if (typeof body.message === "string") {
                    serverMsg = body.message;
                } else if (typeof body.key === "string") {
                    serverMsg = body.key;
                } else {
                    serverMsg = JSON.stringify(body);
                }
            } catch {
                /* ignore non‐JSON or empty */
            }
            const error = new ResponseError(
                request,
                response,
                "StatusCode",
                undefined,
                "Unexpected response status: " + response.status,
                requestErrorExtra,
                serverMsg,
                metadata,
                bodyParsed,
                scopeBinding,
            );
            notifyOperationError(error, ingredients, options);
            throw error;
        }

        // Handle empty body
        const contentLength = response.headers.get("content-length");
        if (
            contentLength === "0" || // explicit zero length
            (contentLength === null && // no header + body is empty
                (await response.clone().text()) === "")
        ) {
            // No Content
            if (response.status === 204) {
                return undefined as Res extends undefined ? void : Res;
            }
            if (ingredients.res) {
                const error = new ResponseError(
                    request,
                    response,
                    "EmptyBody",
                    undefined,
                    "Response body is empty",
                    requestErrorExtra,
                    undefined,
                    undefined,
                    false,
                    scopeBinding,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        // Parse the response body
        let data: unknown;
        try {
            data = await response.json();
        } catch (err) {
            const error = new ResponseError(
                request,
                response,
                "Decode",
                err,
                "Failed to parse response body as JSON",
                requestErrorExtra,
                undefined,
                undefined,
                false,
                scopeBinding,
            );
            notifyOperationError(error, ingredients, options);
            throw error;
        }

        // Validate output
        if (ingredients.res) {
            const resResult = ingredients.res.safeParse(data);
            if (!resResult.success) {
                const error = new ResponseError(
                    request,
                    response,
                    "Decode",
                    resResult.error,
                    "Response body failed validation:" + z.prettifyError(resResult.error),
                    requestErrorExtra,
                    undefined,
                    undefined,
                    true,
                    scopeBinding,
                );
                console.error(error.message, error.cause);
                // do not throw on error
                // Apply date coercion even when validation fails
                return coerceDates(data) as Res extends undefined ? void : Res;
            }
            return resResult.data as Res extends undefined ? void : Res;
        }

        return undefined as Res extends undefined ? void : Res;
    };
}

type StreamEnvelope<Res> = {
    data: Res | null;
    error?: string | null;
};

/**
 * Creates a type-safe API stream call function with validation and error handling.
 * Reads NDJSON envelopes of shape `{ data, error }` from a response stream.
 */
export function createStream<P = undefined, Q = undefined, Req = undefined, Res = undefined>(
    ingredients: {
        p?: z.ZodType<P>;
        q?: z.ZodType<Q>;
        req?: z.ZodType<Req>;
        res?: z.ZodType<Res>;
        pathTemplate: string;
        method: string;
        requiredScopes?: readonly string[];
    },
    fetcher?: (request: Request) => Promise<Response>,
    options?: {
        baseUrl?: string;
        getRequestErrorExtra?: (req: Req) => Record<string, unknown>;
        expectedScope?: ScopeContextBinding;
    } & ErrorNotificationOptions,
) {
    type Arg<P, Q, Req> = [P, Q, Req] extends [undefined, undefined, undefined]
        ? RequestMeta | undefined
        : (P extends undefined ? {} : { params: P }) &
              (Q extends undefined ? {} : { query: Q }) &
              (Req extends undefined ? {} : { req: Req }) &
              RequestMeta;

    return async function (
        args: Arg<P, Q, Req>,
    ): Promise<AsyncIterable<StreamEnvelope<Res extends undefined ? unknown : Res>>> {
        const config = getSpellbookConfig();

        let scope: ReturnType<typeof assertScopeBinding>;
        try {
            scope = assertScopeBinding(ingredients, args?.requestContext, options?.expectedScope);
        } catch (error) {
            if (isSpellbookError(error)) {
                notifyOperationError(error, ingredients, options);
            }
            throw error;
        }
        const scopeBinding = scope.binding;
        const scopeHeaders = scope.headers;

        const requestPath = buildPath<P, Q>(ingredients.pathTemplate)({
            ...(args && "params" in args ? { params: args.params } : {}),
            ...(args && "query" in args ? { query: args.query } : {}),
        } as { params: P; query: Q });
        const baseUrl = options?.baseUrl ?? config.baseUrl;
        if (!baseUrl) {
            throw new Error(
                "No baseUrl provided. Please set a baseUrl in the config or options before making API calls.",
            );
        }
        const url = new URL(requestPath, baseUrl).toString();

        const headersToUse =
            typeof config.headers === "function" ? config.headers() : config.headers;
        const headers = mergeRequestHeaders(headersToUse, args?.headers, scopeHeaders);
        if (args?.idempotencyKey) {
            headers.set("Idempotency-Key", args.idempotencyKey);
        }

        const init: RequestInit = {
            method: ingredients.method,
            headers,
            signal: args?.signal,
            ...(args && "req" in args ? { body: JSON.stringify(args.req) } : {}),
        };

        const request = new Request(url, init);

        const requestErrorExtra = {
            ...(args && "params" in args ? { params: args.params } : {}),
            ...(args && "query" in args ? { query: args.query } : {}),
            ...(args && "req" in args && options?.getRequestErrorExtra
                ? options.getRequestErrorExtra(args.req)
                : {}),
        };

        if (ingredients.p && args && "params" in args) {
            const pResult = ingredients.p.safeParse(args.params);
            if (!pResult.success) {
                const error = new RequestError(
                    request,
                    "Encode",
                    pResult.error,
                    "Path parameters failed validation:" + z.prettifyError(pResult.error),
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        if (ingredients.q && args && "query" in args) {
            const qResult = ingredients.q.safeParse(args.query);
            if (!qResult.success) {
                const error = new RequestError(
                    request,
                    "Encode",
                    qResult.error,
                    "Query parameters failed validation:" + z.prettifyError(qResult.error),
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        if (ingredients.req && args && "req" in args) {
            const reqResult = ingredients.req.safeParse(args.req);
            if (!reqResult.success) {
                const error = new RequestError(
                    request,
                    "Encode",
                    reqResult.error,
                    "Request body failed validation:" + z.prettifyError(reqResult.error),
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
        }

        let response: Response;
        const effectiveFetch =
            fetcher ??
            getSpellbookConfig().fetch ??
            ((nextRequest: Request) => globalThis.fetch(nextRequest));
        try {
            response = await effectiveFetch(request);
        } catch (err) {
            if (err instanceof DOMException && err.name === "AbortError") {
                const error = new RequestError(
                    request,
                    "Abort",
                    err,
                    "Request cancelled by an Abort Signal",
                    requestErrorExtra,
                );
                notifyOperationError(error, ingredients, options);
                throw error;
            }
            const error = new RequestError(
                request,
                "Transport",
                err,
                "Network request failed",
                requestErrorExtra,
            );
            notifyOperationError(error, ingredients, options);
            throw error;
        }

        if (!response.ok) {
            let serverMsg: string | undefined;
            let metadata: Record<string, unknown> | undefined;
            let bodyParsed = false;
            try {
                const body = await response.clone().json();
                bodyParsed = true;
                metadata = body.metadata;
                if (typeof body.message === "string") {
                    serverMsg = body.message;
                } else if (typeof body.key === "string") {
                    serverMsg = body.key;
                } else {
                    serverMsg = JSON.stringify(body);
                }
            } catch {
                /* ignore non-JSON or empty */
            }
            const error = new ResponseError(
                request,
                response,
                "StatusCode",
                undefined,
                "Unexpected response status: " + response.status,
                requestErrorExtra,
                serverMsg,
                metadata,
                bodyParsed,
                scopeBinding,
            );
            notifyOperationError(error, ingredients, options);
            throw error;
        }

        const responseBody = response.body;
        if (!responseBody) {
            const error = new ResponseError(
                request,
                response,
                "EmptyBody",
                undefined,
                "Response body is empty",
                requestErrorExtra,
                undefined,
                undefined,
                false,
                scopeBinding,
            );
            notifyOperationError(error, ingredients, options);
            throw error;
        }

        const payloadSchema = (
            ingredients.res ? ingredients.res.nullable() : z.unknown().nullable()
        ) as z.ZodTypeAny;
        const envelopeSchema = z.object({
            data: payloadSchema,
            error: z.string().nullable().optional(),
        });

        const stream = async function* (): AsyncGenerator<
            StreamEnvelope<Res extends undefined ? unknown : Res>
        > {
            let buffer = "";
            const lineStream = responseBody.pipeThrough(new TextDecoderStream()).pipeThrough(
                new TransformStream<string, string>({
                    transform(chunk, controller) {
                        buffer += chunk;
                        const lines = buffer.split("\n");
                        buffer = lines.pop() || "";
                        for (const rawLine of lines) {
                            const line = rawLine.endsWith("\r")
                                ? rawLine.slice(0, rawLine.length - 1)
                                : rawLine;
                            if (!line.trim()) {
                                continue;
                            }
                            controller.enqueue(line);
                        }
                    },
                    flush(controller) {
                        const rawLine = buffer;
                        const line = rawLine.endsWith("\r")
                            ? rawLine.slice(0, rawLine.length - 1)
                            : rawLine;
                        if (line.trim()) {
                            controller.enqueue(line);
                        }
                    },
                }),
            );
            const lineReader = lineStream.getReader();
            let fullyConsumed = false;

            const parseLine = (
                line: string,
            ): StreamEnvelope<Res extends undefined ? unknown : Res> | undefined => {
                try {
                    const parsed = JSON.parse(line);
                    const envelopeResult = envelopeSchema.safeParse(parsed);
                    if (!envelopeResult.success) {
                        const error = new ResponseError(
                            request,
                            response,
                            "Decode",
                            envelopeResult.error,
                            "Stream item failed validation:" +
                                z.prettifyError(envelopeResult.error),
                            requestErrorExtra,
                            undefined,
                            { line },
                            true,
                            scopeBinding,
                        );
                        console.error(error.message, error.cause);
                        return undefined;
                    }
                    return envelopeResult.data as StreamEnvelope<
                        Res extends undefined ? unknown : Res
                    >;
                } catch (err) {
                    const error = new ResponseError(
                        request,
                        response,
                        "Decode",
                        err,
                        "Failed to parse stream item as JSON",
                        requestErrorExtra,
                        undefined,
                        { line },
                        false,
                        scopeBinding,
                    );
                    notifyOperationError(error, ingredients, options);
                    return undefined;
                }
            };

            try {
                let readResult = await lineReader.read();
                while (!readResult.done) {
                    const line = readResult.value;
                    const parsedLine = parseLine(line);
                    if (parsedLine !== undefined) {
                        yield parsedLine;
                    }
                    readResult = await lineReader.read();
                }
                fullyConsumed = true;
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                const error =
                    err instanceof ResponseError
                        ? err
                        : new RequestError(
                              request,
                              "StreamRead",
                              err,
                              "Failed while reading response stream",
                              requestErrorExtra,
                          );
                notifyOperationError(error, ingredients, options);
                throw error;
            } finally {
                if (!fullyConsumed) {
                    await lineReader.cancel().catch(() => {
                        // Ignore cancellation errors during shutdown.
                    });
                }
                lineReader.releaseLock();
            }
        };

        return stream();
    };
}
// This file is a Go template for the dynamic endpoints and spellbook function.
// The static TypeScript code is in spellbooktpl/spellbook_static.ts and must be concatenated before this template when generating the output.

import * as schemas from "./schemas";

const endpoints = {
    "/attendances": {
        GET: function () {
            const q = schemas.api_pkg_attendance_dto_GetAttendanceParamsSchema;
            const res = schemas.api_pkg_attendance_dto_GetAttendanceRespSchema;
            type Q = z.infer<typeof schemas.api_pkg_attendance_dto_GetAttendanceParamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_attendance_dto_GetAttendanceRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/attendances" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                q,
                res,
            };
        },
        POST: function () {
            const req = schemas.api_pkg_attendance_dto_PostAttendancesParamsSchema;
            type Req = z.infer<typeof schemas.api_pkg_attendance_dto_PostAttendancesParamsSchema>;
            const types = {
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/attendances" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
            };
        },
        DELETE: function () {
            const req = schemas.api_pkg_attendance_dto_DeleteAttendancesReqSchema;
            const res = schemas.api_pkg_attendance_dto_DeleteAttendancesRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_attendance_dto_DeleteAttendancesReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_attendance_dto_DeleteAttendancesRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/attendances" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
                res,
            };
        },
    },
    "/attendances/{id}/dispute": {
        POST: function () {
            const p = schemas.api_cmd_api_attendance_PostAttendanceDisputePathparamsSchema;
            const req = schemas.api_pkg_attendance_dto_PostAttendanceDisputeSchema;
            const res = schemas.api_cmd_api_attendance_PostAttendanceDisputeResponseSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_attendance_PostAttendanceDisputePathparamsSchema
            >;
            type Req = z.infer<typeof schemas.api_pkg_attendance_dto_PostAttendanceDisputeSchema>;
            type Res = z.infer<
                typeof schemas.api_cmd_api_attendance_PostAttendanceDisputeResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/attendances/{id}/dispute" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/auth/account": {
        POST: function () {
            const req = schemas.api_pkg_auth_dto_CreateAccountReqSchema;
            const res = schemas.api_pkg_auth_dto_LoginRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_CreateAccountReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_LoginRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/account" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                req,
                res,
            };
        },
    },
    "/auth/google/signup": {
        POST: function () {
            const req = schemas.api_pkg_auth_dto_GoogleSignUpReqSchema;
            const res = schemas.api_pkg_auth_dto_LoginRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_GoogleSignUpReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_LoginRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/google/signup" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                req,
                res,
            };
        },
    },
    "/auth/google/token": {
        POST: function () {
            const req = schemas.api_pkg_auth_dto_GoogleLoginReqSchema;
            const res = schemas.api_pkg_auth_dto_LoginRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_GoogleLoginReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_LoginRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/google/token" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                req,
                res,
            };
        },
    },
    "/auth/google/verify": {
        POST: function () {
            const pathTemplate = "/auth/google/verify" as const;
            const method = "POST" as const;

            return {
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["authenticated"] as const,
            };
        },
    },
    "/auth/login": {
        POST: function () {
            const req = schemas.api_pkg_auth_dto_LoginReqSchema;
            const res = schemas.api_pkg_auth_dto_LoginRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_LoginReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_LoginRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/login" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                req,
                res,
            };
        },
    },
    "/auth/signup": {
        POST: function () {
            const req = schemas.api_pkg_auth_dto_SignUpReqSchema;
            const res = schemas.api_pkg_auth_dto_SignUpRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_SignUpReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_SignUpRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/signup" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                req,
                res,
            };
        },
    },
    "/auth/users/{id}/activate": {
        PUT: function () {
            const p = schemas.api_cmd_api_auth_ActivateUserPathparamsSchema;
            const req = schemas.api_pkg_auth_dto_ActivateUserReqSchema;
            const res = schemas.api_pkg_auth_dto_LoginRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_auth_ActivateUserPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_ActivateUserReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_LoginRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/users/{id}/activate" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                p,
                req,
                res,
            };
        },
    },
    "/auth/users/{id}/google/activate": {
        PUT: function () {
            const p = schemas.api_cmd_api_auth_GoogleActivateUserPathparamsSchema;
            const req = schemas.api_pkg_auth_dto_GoogleActivateUserReqSchema;
            const res = schemas.api_pkg_auth_dto_LoginRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_auth_GoogleActivateUserPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_auth_dto_GoogleActivateUserReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_auth_dto_LoginRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/auth/users/{id}/google/activate" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                p,
                req,
                res,
            };
        },
    },
    "/contracts/{contractId}/weeklyduration": {
        PUT: function () {
            const p = schemas.team_api_controllers_contract_SetFixedWeeklyDurationPathparamsSchema;
            const req = schemas.v3_pkg_contract_dto_SetFixedWeeklyDurationReqSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_contract_SetFixedWeeklyDurationPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.v3_pkg_contract_dto_SetFixedWeeklyDurationReqSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/contracts/{contractId}/weeklyduration" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
            };
        },
    },
    "/custom-job-titles": {
        GET: function () {
            const res = schemas.api_cmd_api_jobdescription_ListCustomJobTitlesResponseSchema;
            type Res = z.infer<
                typeof schemas.api_cmd_api_jobdescription_ListCustomJobTitlesResponseSchema
            >;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/custom-job-titles" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                res,
            };
        },
    },
    "/download/{type}/{id}": {
        GET: function () {
            const p = schemas.gitlab_side_co_pkg_sides3proxy_DownloadHandlerPathparamsSchema;
            type P = z.infer<
                typeof schemas.gitlab_side_co_pkg_sides3proxy_DownloadHandlerPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/download/{type}/{id}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                p,
            };
        },
    },
    "/guest/feedbacks": {
        POST: function () {
            const q = schemas.team_api_controllers_feedback_PostGuestFeedbackQuerySchema;
            const req = schemas.team_api_src_types_FeedbackBodySchema;
            const res = schemas.team_api_controllers_feedback_PostGuestFeedbackResponseSchema;
            type Q = z.infer<
                typeof schemas.team_api_controllers_feedback_PostGuestFeedbackQuerySchema
            >;
            type Req = z.infer<typeof schemas.team_api_src_types_FeedbackBodySchema>;
            type Res = z.infer<
                typeof schemas.team_api_controllers_feedback_PostGuestFeedbackResponseSchema
            >;
            const types = {
                Q: undefined as unknown as Q,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/guest/feedbacks" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                q,
                req,
                res,
            };
        },
    },
    "/guest/tasks/{id}": {
        GET: function () {
            const p = schemas.api_cmd_api_task_GetGuestTaskPathparamsSchema;
            const q = schemas.api_cmd_api_task_GetGuestTaskQuerySchema;
            const res = schemas.api_pkg_task_dto_GuestTaskSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_GetGuestTaskPathparamsSchema>;
            type Q = z.infer<typeof schemas.api_cmd_api_task_GetGuestTaskQuerySchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_GuestTaskSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/guest/tasks/{id}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: [] as const,
                p,
                q,
                res,
            };
        },
    },
    "/invoice/order-form-mention": {
        PUT: function () {
            const req = schemas.charges_pkg_invoice_dto_PutInvoiceOrderFormMentionReqSchema;
            const res = schemas.Services_charges_pkg_invoice_ISchema;
            type Req = z.infer<
                typeof schemas.charges_pkg_invoice_dto_PutInvoiceOrderFormMentionReqSchema
            >;
            type Res = z.infer<typeof schemas.Services_charges_pkg_invoice_ISchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/invoice/order-form-mention" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
                res,
            };
        },
    },
    "/job-descriptions": {
        GET: function () {
            const res = schemas.api_pkg_jobdescription_dto_ListJobDescriptionsDetailsRespSchema;
            type Res = z.infer<
                typeof schemas.api_pkg_jobdescription_dto_ListJobDescriptionsDetailsRespSchema
            >;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                res,
            };
        },
        POST: function () {
            const req = schemas.api_pkg_jobdescription_dto_PostJobDescriptionReqSchema;
            const res = schemas.api_pkg_jobdescription_dto_JobDescriptionsDetailsSchema;
            type Req = z.infer<
                typeof schemas.api_pkg_jobdescription_dto_PostJobDescriptionReqSchema
            >;
            type Res = z.infer<
                typeof schemas.api_pkg_jobdescription_dto_JobDescriptionsDetailsSchema
            >;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                req,
                res,
            };
        },
    },
    "/job-descriptions/job-titles": {
        GET: function () {
            const res = schemas.api_cmd_api_jobdescription_ListJobTitlesResponseSchema;
            type Res = z.infer<
                typeof schemas.api_cmd_api_jobdescription_ListJobTitlesResponseSchema
            >;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions/job-titles" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                res,
            };
        },
    },
    "/job-descriptions/job-titles/{id}/subtasks": {
        GET: function () {
            const p = schemas.api_cmd_api_jobdescription_ListJobTitleSubtasksPathparamsSchema;
            const res = schemas.api_cmd_api_jobdescription_ListJobTitleSubtasksResponseSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_jobdescription_ListJobTitleSubtasksPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.api_cmd_api_jobdescription_ListJobTitleSubtasksResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions/job-titles/{id}/subtasks" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/job-descriptions/options": {
        GET: function () {
            const res = schemas.api_cmd_api_jobdescription_ListOptionsResponseSchema;
            type Res = z.infer<typeof schemas.api_cmd_api_jobdescription_ListOptionsResponseSchema>;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions/options" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                res,
            };
        },
    },
    "/job-descriptions/{id}": {
        PATCH: function () {
            const p = schemas.api_cmd_api_jobdescription_PatchJobDescriptionPathparamsSchema;
            const req = schemas.api_pkg_jobdescription_dto_PatchJobDescriptionReqSchema;
            const res = schemas.api_pkg_jobdescription_dto_JobDescriptionsDetailsSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_jobdescription_PatchJobDescriptionPathparamsSchema
            >;
            type Req = z.infer<
                typeof schemas.api_pkg_jobdescription_dto_PatchJobDescriptionReqSchema
            >;
            type Res = z.infer<
                typeof schemas.api_pkg_jobdescription_dto_JobDescriptionsDetailsSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions/{id}" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
        DELETE: function () {
            const p = schemas.api_cmd_api_jobdescription_DelJobDescriptionPathparamsSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_jobdescription_DelJobDescriptionPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/job-descriptions/{id}" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
            };
        },
    },
    "/job-descriptions/{id}/tasks": {
        POST: function () {
            const p = schemas.api_cmd_api_task_PostFromJobDescriptionPathparamsSchema;
            const req = schemas.api_pkg_task_dto_PostTaskReqSchema;
            const res = schemas.api_pkg_task_dto_TSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_task_PostFromJobDescriptionPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PostTaskReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_TSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/job-descriptions/{id}/tasks" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/legal-values": {
        GET: function () {
            const res = schemas.api_pkg_legalvalue_dto_LegalValuesRespSchema;
            type Res = z.infer<typeof schemas.api_pkg_legalvalue_dto_LegalValuesRespSchema>;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/legal-values" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["authenticated"] as const,
                res,
            };
        },
    },
    "/me/organisation/completion": {
        GET: function () {
            const res = schemas.api_pkg_organisation_dto_GetCompletionStatusRespSchema;
            type Res = z.infer<
                typeof schemas.api_pkg_organisation_dto_GetCompletionStatusRespSchema
            >;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisation/completion" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                res,
            };
        },
    },
    "/me/organisation/siders": {
        POST: function () {
            const req = schemas.api_pkg_organisation_dto_PostSiderReqSchema;
            type Req = z.infer<typeof schemas.api_pkg_organisation_dto_PostSiderReqSchema>;
            const types = {
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/me/organisation/siders" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
            };
        },
    },
    "/me/organisations/documents": {
        GET: function () {
            const q = schemas.api_pkg_contract_dto_ListDocumentsReqSchema;
            const res = schemas.api_pkg_contract_dto_ListDocumentsRespSchema;
            type Q = z.infer<typeof schemas.api_pkg_contract_dto_ListDocumentsReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_contract_dto_ListDocumentsRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/documents" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                q,
                res,
            };
        },
    },
    "/me/organisations/features-policy": {
        GET: function () {
            const res = schemas.Services_companies_pkg_featurespolicy_FPSchema;
            type Res = z.infer<typeof schemas.Services_companies_pkg_featurespolicy_FPSchema>;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/features-policy" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                res,
            };
        },
    },
    "/me/organisations/holidays": {
        GET: function () {
            const q = schemas.api_pkg_organisation_dto_ListHolidaysReqSchema;
            const res = schemas.api_pkg_organisation_dto_ListHolidaysRespSchema;
            type Q = z.infer<typeof schemas.api_pkg_organisation_dto_ListHolidaysReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_organisation_dto_ListHolidaysRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/holidays" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                q,
                res,
            };
        },
    },
    "/me/organisations/picture/{type}": {
        POST: function () {
            const p = schemas.api_cmd_api_organisation_UploadHandlerPathparamsSchema;
            const res = schemas.api_pkg_organisation_dto_UploadPictureRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_organisation_UploadHandlerPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_organisation_dto_UploadPictureRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/picture/{type}" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/me/organisations/profile": {
        GET: function () {
            const res = schemas.api_pkg_organisation_dto_GetOrganisationProfileRespSchema;
            type Res = z.infer<
                typeof schemas.api_pkg_organisation_dto_GetOrganisationProfileRespSchema
            >;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/profile" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                res,
            };
        },
        PATCH: function () {
            const req = schemas.api_pkg_organisation_dto_PatchOrganisationProfileReqSchema;
            const res = schemas.api_pkg_organisation_dto_GetOrganisationProfileRespSchema;
            type Req = z.infer<
                typeof schemas.api_pkg_organisation_dto_PatchOrganisationProfileReqSchema
            >;
            type Res = z.infer<
                typeof schemas.api_pkg_organisation_dto_GetOrganisationProfileRespSchema
            >;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/profile" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                req,
                res,
            };
        },
    },
    "/me/organisations/users": {
        GET: function () {
            const q = schemas.api_pkg_user_dto_ListUsersReqSchema;
            const res = schemas.api_pkg_user_dto_ListUsersRespSchema;
            type Q = z.infer<typeof schemas.api_pkg_user_dto_ListUsersReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_user_dto_ListUsersRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/users" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                q,
                res,
            };
        },
        POST: function () {
            const req = schemas.api_pkg_user_dto_PostUserReqSchema;
            const res = schemas.api_pkg_user_dto_ListUsersRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_user_dto_PostUserReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_user_dto_ListUsersRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/users" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                req,
                res,
            };
        },
    },
    "/me/organisations/users/{id}": {
        PATCH: function () {
            const p = schemas.api_cmd_api_user_PatchUserPathparamsSchema;
            const req = schemas.api_pkg_user_dto_PatchUserReqSchema;
            const res = schemas.api_pkg_user_dto_ListUsersRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_user_PatchUserPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_user_dto_PatchUserReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_user_dto_ListUsersRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/users/{id}" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
        DELETE: function () {
            const p = schemas.api_cmd_api_user_DeleteUserPathparamsSchema;
            const res = schemas.api_pkg_user_dto_ListUsersRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_user_DeleteUserPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_user_dto_ListUsersRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/organisations/users/{id}" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/me/organisations/users/{id}/invite": {
        POST: function () {
            const p = schemas.api_cmd_api_user_ReInviteUserPathparamsSchema;
            type P = z.infer<typeof schemas.api_cmd_api_user_ReInviteUserPathparamsSchema>;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/me/organisations/users/{id}/invite" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
            };
        },
    },
    "/me/profile": {
        PATCH: function () {
            const req = schemas.api_pkg_user_dto_PatchUserProfileReqSchema;
            const res = schemas.api_pkg_user_dto_PatchUserProfileRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_user_dto_PatchUserProfileReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_user_dto_PatchUserProfileRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/profile" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["authenticated"] as const,
                req,
                res,
            };
        },
    },
    "/me/tasks": {
        GET: function () {
            const q = schemas.api_pkg_task_dto_ListTasksReqSchema;
            const res = schemas.api_pkg_task_dto_ListRespSchema;
            type Q = z.infer<typeof schemas.api_pkg_task_dto_ListTasksReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ListRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/me/tasks" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                q,
                res,
            };
        },
    },
    "/motives": {
        GET: function () {
            const res = schemas.api_cmd_api_motive_ListMotivesResponseSchema;
            type Res = z.infer<typeof schemas.api_cmd_api_motive_ListMotivesResponseSchema>;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/motives" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                res,
            };
        },
        POST: function () {
            const req = schemas.Services_lego_pkg_motives_MSchema;
            const res = schemas.api_cmd_api_motive_PostMotiveResponseSchema;
            type Req = z.infer<typeof schemas.Services_lego_pkg_motives_MSchema>;
            type Res = z.infer<typeof schemas.api_cmd_api_motive_PostMotiveResponseSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/motives" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
                res,
            };
        },
    },
    "/motives/{id}": {
        DELETE: function () {
            const p = schemas.api_cmd_api_motive_DelMotivePathparamsSchema;
            type P = z.infer<typeof schemas.api_cmd_api_motive_DelMotivePathparamsSchema>;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/motives/{id}" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
    },
    "/organisations/children/search": {
        GET: function () {
            const q = schemas.Services_team_api_dto_ChildrenSearchReqSchema;
            const res = schemas.Services_team_api_dto_ChildrenSearchRespSchema;
            type Q = z.infer<typeof schemas.Services_team_api_dto_ChildrenSearchReqSchema>;
            type Res = z.infer<typeof schemas.Services_team_api_dto_ChildrenSearchRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/children/search" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["network"] as const,
                q,
                res,
            };
        },
    },
    "/organisations/{organisationId}/children": {
        GET: function () {
            const p = schemas.team_api_controllers_organisations_GetChildrenPathparamsSchema;
            const res = schemas.team_api_controllers_organisations_GetChildrenResponseSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_GetChildrenPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.team_api_controllers_organisations_GetChildrenResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/children" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/confirm_hours-types": {
        PUT: function () {
            const p = schemas.team_api_controllers_remuneration_ConfirmHourTypesPathparamsSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_remuneration_ConfirmHourTypesPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/organisations/{organisationId}/confirm_hours-types" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
    },
    "/organisations/{organisationId}/confirm_indemnities": {
        PUT: function () {
            const p = schemas.team_api_controllers_remuneration_ConfirmIndemnitiesPathparamsSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_remuneration_ConfirmIndemnitiesPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/organisations/{organisationId}/confirm_indemnities" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
    },
    "/organisations/{organisationId}/departments": {
        POST: function () {
            const p = schemas.api_cmd_api_department_PostPathparamsSchema;
            const req = schemas.api_pkg_department_dto_PostDepartmentReqSchema;
            const res = schemas.api_cmd_api_department_PostResponseSchema;
            type P = z.infer<typeof schemas.api_cmd_api_department_PostPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_department_dto_PostDepartmentReqSchema>;
            type Res = z.infer<typeof schemas.api_cmd_api_department_PostResponseSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/departments" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
        GET: function () {
            const p = schemas.api_cmd_api_department_ListPathparamsSchema;
            const q = schemas.api_pkg_department_dto_ListDepartmentsReqSchema;
            const res = schemas.api_pkg_department_dto_DepartmentsSchema;
            type P = z.infer<typeof schemas.api_cmd_api_department_ListPathparamsSchema>;
            type Q = z.infer<typeof schemas.api_pkg_department_dto_ListDepartmentsReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_department_dto_DepartmentsSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/departments" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                q,
                res,
            };
        },
    },
    "/organisations/{organisationId}/departments/{id}": {
        DELETE: function () {
            const p = schemas.api_cmd_api_department_DeletePathparamsSchema;
            type P = z.infer<typeof schemas.api_cmd_api_department_DeletePathparamsSchema>;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/organisations/{organisationId}/departments/{id}" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
        GET: function () {
            const p = schemas.api_cmd_api_department_GetPathparamsSchema;
            const res = schemas.api_cmd_api_department_GetResponseSchema;
            type P = z.infer<typeof schemas.api_cmd_api_department_GetPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_cmd_api_department_GetResponseSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/departments/{id}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/document/{documentId}/contest": {
        PUT: function () {
            const p = schemas.team_api_controllers_contract_ContestDocumentPathparamsSchema;
            const req = schemas.v3_pkg_document_dto_ContestReqSchema;
            const res = schemas.v3_pkg_document_dto_ContestRespSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_contract_ContestDocumentPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.v3_pkg_document_dto_ContestReqSchema>;
            type Res = z.infer<typeof schemas.v3_pkg_document_dto_ContestRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate =
                "/organisations/{organisationId}/document/{documentId}/contest" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/organisations/{organisationId}/document/{documentId}/sign": {
        PUT: function () {
            const p = schemas.team_api_controllers_contract_SignDocumentPathparamsSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_contract_SignDocumentPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate =
                "/organisations/{organisationId}/document/{documentId}/sign" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
            };
        },
    },
    "/organisations/{organisationId}/documents": {
        GET: function () {
            const p = schemas.team_api_controllers_contract_ListDocumentsPathparamsSchema;
            const q = schemas.v3_pkg_document_dto_ListReqSchema;
            const res = schemas.api_pkg_contract_dto_ListDocumentsRespSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_contract_ListDocumentsPathparamsSchema
            >;
            type Q = z.infer<typeof schemas.v3_pkg_document_dto_ListReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_contract_dto_ListDocumentsRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/documents" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                q,
                res,
            };
        },
    },
    "/organisations/{organisationId}/documents/regenerate": {
        PUT: function () {
            const p = schemas.team_api_controllers_contract_RegenerateDocumentsPathparamsSchema;
            const req = schemas.Services_team_api_dto_RegenerateDocumentsReqSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_contract_RegenerateDocumentsPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.Services_team_api_dto_RegenerateDocumentsReqSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/organisations/{organisationId}/documents/regenerate" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork", "logas"] as const,
                p,
                req,
            };
        },
    },
    "/organisations/{organisationId}/documents/unsigned/count": {
        GET: function () {
            const p =
                schemas.team_api_controllers_contract_GetCountUnsignedDocumentsByOrganisationPathparamsSchema;
            const res = schemas.v3_pkg_document_dto_CountRespSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_contract_GetCountUnsignedDocumentsByOrganisationPathparamsSchema
            >;
            type Res = z.infer<typeof schemas.v3_pkg_document_dto_CountRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate =
                "/organisations/{organisationId}/documents/unsigned/count" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/hours-types": {
        PATCH: function () {
            const p = schemas.team_api_controllers_remuneration_PatchHourTypesPathparamsSchema;
            const req = schemas.Services_team_api_dto_HoursTypesSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_remuneration_PatchHourTypesPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.Services_team_api_dto_HoursTypesSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/organisations/{organisationId}/hours-types" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
            };
        },
    },
    "/organisations/{organisationId}/indemnities": {
        PATCH: function () {
            const p = schemas.team_api_controllers_remuneration_PatchIndemnitiesPathparamsSchema;
            const req = schemas.Services_team_api_dto_PatchIndemnitiesParamsSchema;
            const res = schemas.Services_team_api_dto_IndemnitiesSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_remuneration_PatchIndemnitiesPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.Services_team_api_dto_PatchIndemnitiesParamsSchema>;
            type Res = z.infer<typeof schemas.Services_team_api_dto_IndemnitiesSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/indemnities" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/organisations/{organisationId}/job_types": {
        GET: function () {
            const p = schemas.team_api_controllers_jobtypes_ListPathparamsSchema;
            const res = schemas.Services_team_api_dto_GetJobTypesRespSchema;
            type P = z.infer<typeof schemas.team_api_controllers_jobtypes_ListPathparamsSchema>;
            type Res = z.infer<typeof schemas.Services_team_api_dto_GetJobTypesRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/job_types" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/legal-informations": {
        GET: function () {
            const p =
                schemas.team_api_controllers_organisations_GetLegalInformationsPathparamsSchema;
            const res =
                schemas.team_api_controllers_organisations_GetLegalInformationsResponseSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_GetLegalInformationsPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.team_api_controllers_organisations_GetLegalInformationsResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/legal-informations" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
        PATCH: function () {
            const p =
                schemas.team_api_controllers_organisations_PatchLegalInformationsPathparamsSchema;
            const req = schemas.Services_team_api_dto_PatchLegalInformationsSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_PatchLegalInformationsPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.Services_team_api_dto_PatchLegalInformationsSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/organisations/{organisationId}/legal-informations" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
            };
        },
    },
    "/organisations/{organisationId}/locations": {
        POST: function () {
            const p = schemas.team_api_controllers_organisations_PostLocationPathparamsSchema;
            const req = schemas.Services_team_api_dto_PostLocationReqSchema;
            const res = schemas.Services_locations_pkg_location_LSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_PostLocationPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.Services_team_api_dto_PostLocationReqSchema>;
            type Res = z.infer<typeof schemas.Services_locations_pkg_location_LSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/locations" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
        GET: function () {
            const p = schemas.team_api_controllers_organisations_ListLocationsPathparamsSchema;
            const res = schemas.locations_pkg_location_dto_ListLocationsRespSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_ListLocationsPathparamsSchema
            >;
            type Res = z.infer<typeof schemas.locations_pkg_location_dto_ListLocationsRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/locations" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/locations/{locationId}": {
        PATCH: function () {
            const p = schemas.team_api_controllers_organisations_PatchLocationPathparamsSchema;
            const req = schemas.Services_team_api_dto_PostLocationReqSchema;
            const res = schemas.Services_locations_pkg_location_LSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_PatchLocationPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.Services_team_api_dto_PostLocationReqSchema>;
            type Res = z.infer<typeof schemas.Services_locations_pkg_location_LSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/locations/{locationId}" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/organisations/{organisationId}/payment-informations": {
        GET: function () {
            const p = schemas.api_cmd_api_organisation_GetPaymentInformationsPathparamsSchema;
            const res = schemas.api_pkg_organisation_dto_GetPaymentInformationsRespSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_organisation_GetPaymentInformationsPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.api_pkg_organisation_dto_GetPaymentInformationsRespSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/payment-informations" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
        PATCH: function () {
            const p =
                schemas.team_api_controllers_organisations_PatchPaymentInformationsPathparamsSchema;
            const req = schemas.team_api_controllers_organisations_PatchPaymentInformationsSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_organisations_PatchPaymentInformationsPathparamsSchema
            >;
            type Req = z.infer<
                typeof schemas.team_api_controllers_organisations_PatchPaymentInformationsSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/organisations/{organisationId}/payment-informations" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
            };
        },
    },
    "/organisations/{organisationId}/payment-method": {
        DELETE: function () {
            const p = schemas.api_cmd_api_organisation_ResetPaymentMethodPathparamsSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_organisation_ResetPaymentMethodPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/organisations/{organisationId}/payment-method" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
    },
    "/organisations/{organisationId}/remuneration-policy": {
        GET: function () {
            const p =
                schemas.team_api_controllers_remuneration_GetRemunerationPolicyPathparamsSchema;
            const res = schemas.Services_team_api_dto_GetRemunerationPolicyRespSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_remuneration_GetRemunerationPolicyPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.Services_team_api_dto_GetRemunerationPolicyRespSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/remuneration-policy" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/remuneration-policy-settings": {
        GET: function () {
            const p =
                schemas.api_cmd_api_organisation_GetRemunerationPolicySettingsPathparamsSchema;
            const res =
                schemas.api_cmd_api_organisation_GetRemunerationPolicySettingsResponseSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_organisation_GetRemunerationPolicySettingsPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.api_cmd_api_organisation_GetRemunerationPolicySettingsResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate =
                "/organisations/{organisationId}/remuneration-policy-settings" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
        PATCH: function () {
            const p =
                schemas.api_cmd_api_organisation_PatchRemunerationPolicySettingsPathparamsSchema;
            const req = schemas.api_pkg_organisation_dto_PatchRemunerationPolicyReqSchema;
            const res =
                schemas.api_cmd_api_organisation_PatchRemunerationPolicySettingsResponseSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_organisation_PatchRemunerationPolicySettingsPathparamsSchema
            >;
            type Req = z.infer<
                typeof schemas.api_pkg_organisation_dto_PatchRemunerationPolicyReqSchema
            >;
            type Res = z.infer<
                typeof schemas.api_cmd_api_organisation_PatchRemunerationPolicySettingsResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate =
                "/organisations/{organisationId}/remuneration-policy-settings" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/organisations/{organisationId}/tasks-names": {
        GET: function () {
            const p = schemas.team_api_controllers_tasks_ListTasksNamesPathparamsSchema;
            const res = schemas.Services_team_api_dto_GetTasksNameRespSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_tasks_ListTasksNamesPathparamsSchema
            >;
            type Res = z.infer<typeof schemas.Services_team_api_dto_GetTasksNameRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/tasks-names" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                res,
            };
        },
    },
    "/organisations/{organisationId}/vms-configuration": {
        GET: function () {
            const p = schemas.api_cmd_api_organisation_GetVMSConfigurationPathparamsSchema;
            const res = schemas.vmx_pkg_vmsconfiguration_dto_VMSConfigurationSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_organisation_GetVMSConfigurationPathparamsSchema
            >;
            type Res = z.infer<typeof schemas.vmx_pkg_vmsconfiguration_dto_VMSConfigurationSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/organisations/{organisationId}/vms-configuration" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/permanent-contracts/requests": {
        POST: function () {
            const req = schemas.Services_team_api_dto_PostPermanentContractRequestSchema;
            type Req = z.infer<
                typeof schemas.Services_team_api_dto_PostPermanentContractRequestSchema
            >;
            const types = {
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/permanent-contracts/requests" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
            };
        },
    },
    "/search/job-titles": {
        GET: function () {
            const q = schemas.api_pkg_search_dto_SearchJobTitlesReqSchema;
            const res = schemas.api_cmd_api_search_SearchJobTitlesResponseSchema;
            type Q = z.infer<typeof schemas.api_pkg_search_dto_SearchJobTitlesReqSchema>;
            type Res = z.infer<typeof schemas.api_cmd_api_search_SearchJobTitlesResponseSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/search/job-titles" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                q,
                res,
            };
        },
    },
    "/shift-recurrences/{id}": {
        GET: function () {
            const p = schemas.api_cmd_api_task_GetShiftRecurrencePathparamsSchema;
            const res = schemas.api_pkg_task_dto_GetShiftRecurrenceRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_GetShiftRecurrencePathparamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_GetShiftRecurrenceRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/shift-recurrences/{id}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/shifts": {
        DELETE: function () {
            const req = schemas.api_pkg_task_dto_DeleteShiftsReqSchema;
            const res = schemas.api_pkg_task_dto_ShiftsRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_DeleteShiftsReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ShiftsRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/shifts" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                req,
                res,
            };
        },
    },
    "/shifts/{id}": {
        DELETE: function () {
            const p = schemas.api_cmd_api_task_DeleteShiftPathparamsSchema;
            const q = schemas.api_cmd_api_task_DeleteShiftQuerySchema;
            const res = schemas.api_pkg_task_dto_ShiftsRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_DeleteShiftPathparamsSchema>;
            type Q = z.infer<typeof schemas.api_cmd_api_task_DeleteShiftQuerySchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ShiftsRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/shifts/{id}" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                q,
                res,
            };
        },
        PATCH: function () {
            const p = schemas.api_cmd_api_task_PatchShiftPathparamsSchema;
            const q = schemas.api_cmd_api_task_PatchShiftQuerySchema;
            const req = schemas.api_pkg_task_dto_PatchShiftReqSchema;
            const res = schemas.api_pkg_task_dto_ShiftsRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_PatchShiftPathparamsSchema>;
            type Q = z.infer<typeof schemas.api_cmd_api_task_PatchShiftQuerySchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PatchShiftReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ShiftsRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/shifts/{id}" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                q,
                req,
                res,
            };
        },
    },
    "/siders/search": {
        GET: function () {
            const q = schemas.api_pkg_sider_dto_ListSidersReqSchema;
            const res = schemas.api_pkg_sider_dto_ListSidersRespSchema;
            type Q = z.infer<typeof schemas.api_pkg_sider_dto_ListSidersReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_sider_dto_ListSidersRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/siders/search" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                q,
                res,
            };
        },
    },
    "/siders/{siderId}": {
        GET: function () {
            const p = schemas.team_api_controllers_siders_GetSiderPathparamsSchema;
            const q = schemas.Services_team_api_dto_GetSiderReqSchema;
            const res = schemas.Services_team_api_dto_GetSiderRespSchema;
            type P = z.infer<typeof schemas.team_api_controllers_siders_GetSiderPathparamsSchema>;
            type Q = z.infer<typeof schemas.Services_team_api_dto_GetSiderReqSchema>;
            type Res = z.infer<typeof schemas.Services_team_api_dto_GetSiderRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/siders/{siderId}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                q,
                res,
            };
        },
    },
    "/tasks/{id}": {
        PATCH: function () {
            const p = schemas.api_cmd_api_task_PatchPathparamsSchema;
            const req = schemas.api_pkg_task_dto_PatchTaskReqSchema;
            const res = schemas.api_pkg_task_dto_TSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_PatchPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PatchTaskReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_TSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
        GET: function () {
            const p = schemas.api_cmd_api_task_GetPathparamsSchema;
            const res = schemas.api_pkg_task_dto_TSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_GetPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_TSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
        DELETE: function () {
            const p = schemas.api_cmd_api_task_DelPathparamsSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_DelPathparamsSchema>;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/tasks/{id}" as const;
            const method = "DELETE" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
    },
    "/tasks/{id}/applicants": {
        GET: function () {
            const p = schemas.api_cmd_api_task_ListApplicantsPathparamsSchema;
            const res = schemas.api_cmd_api_task_ListApplicantsResponseSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_ListApplicantsPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_cmd_api_task_ListApplicantsResponseSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/applicants" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/tasks/{id}/cancel": {
        PATCH: function () {
            const p = schemas.api_cmd_api_task_CancelTaskPathparamsSchema;
            const req = schemas.api_pkg_task_dto_CancelTaskReqSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_CancelTaskPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_CancelTaskReqSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/tasks/{id}/cancel" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
            };
        },
    },
    "/tasks/{id}/clone": {
        POST: function () {
            const p = schemas.api_cmd_api_task_ClonePathparamsSchema;
            const req = schemas.api_pkg_task_dto_PostTaskReqSchema;
            const res = schemas.api_pkg_task_dto_TSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_ClonePathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PostTaskReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_TSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/clone" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/tasks/{id}/confirm": {
        POST: function () {
            const p = schemas.api_cmd_api_task_ConfirmPathparamsSchema;
            const req = schemas.api_pkg_task_dto_ConfirmTaskSchema;
            const res = schemas.api_pkg_task_dto_TSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_ConfirmPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_ConfirmTaskSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_TSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/confirm" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/tasks/{id}/confirm-automations": {
        POST: function () {
            const p = schemas.api_cmd_api_task_RunConfirmAutomationsPathparamsSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_RunConfirmAutomationsPathparamsSchema>;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/tasks/{id}/confirm-automations" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
            };
        },
    },
    "/tasks/{id}/flexible-planning": {
        POST: function () {
            const p = schemas.api_cmd_api_task_PostFlexiblePlanningPathparamsSchema;
            const req = schemas.api_pkg_task_dto_PostFlexiblePlanningReqSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_PostFlexiblePlanningPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PostFlexiblePlanningReqSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/tasks/{id}/flexible-planning" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
            };
        },
        GET: function () {
            const p = schemas.api_cmd_api_task_GetFlexiblePlanningPathparamsSchema;
            const res = schemas.api_pkg_task_dto_GetFlexiblePlanningRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_GetFlexiblePlanningPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_GetFlexiblePlanningRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/flexible-planning" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/tasks/{id}/flexible-planning/quotation": {
        GET: function () {
            const p = schemas.api_cmd_api_task_GetFlexiblePlanningQuotationPathparamsSchema;
            const q = schemas.api_pkg_task_dto_GetFlexiblePlanningQuotationReqSchema;
            const res = schemas.api_pkg_task_dto_GetFlexiblePlanningQuotationRespSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_task_GetFlexiblePlanningQuotationPathparamsSchema
            >;
            type Q = z.infer<typeof schemas.api_pkg_task_dto_GetFlexiblePlanningQuotationReqSchema>;
            type Res = z.infer<
                typeof schemas.api_pkg_task_dto_GetFlexiblePlanningQuotationRespSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/flexible-planning/quotation" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                q,
                res,
            };
        },
    },
    "/tasks/{id}/planning": {
        PATCH: function () {
            const p = schemas.api_cmd_api_task_PatchPlanningPathparamsSchema;
            const req = schemas.api_pkg_task_dto_PatchPlanningReqSchema;
            const res = schemas.api_pkg_task_dto_PatchPlanningRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_PatchPlanningPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PatchPlanningReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_PatchPlanningRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/planning" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/tasks/{id}/quotation": {
        GET: function () {
            const p = schemas.api_cmd_api_task_GetQuotationPathparamsSchema;
            const res = schemas.api_pkg_task_dto_GetQuotationRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_GetQuotationPathparamsSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_GetQuotationRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/quotation" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/tasks/{id}/shifts": {
        POST: function () {
            const p = schemas.api_cmd_api_task_PostShiftsPathparamsSchema;
            const req = schemas.api_pkg_task_dto_PostShiftsReqSchema;
            const res = schemas.api_pkg_task_dto_ShiftsRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_PostShiftsPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_pkg_task_dto_PostShiftsReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ShiftsRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/shifts" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
        PATCH: function () {
            const p = schemas.api_cmd_api_task_PatchShiftsPathparamsSchema;
            const req = schemas.api_cmd_api_task_PatchShiftsRequestSchema;
            const res = schemas.api_pkg_task_dto_ShiftsRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_task_PatchShiftsPathparamsSchema>;
            type Req = z.infer<typeof schemas.api_cmd_api_task_PatchShiftsRequestSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ShiftsRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/shifts" as const;
            const method = "PATCH" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/tasks/{id}/siders": {
        GET: function () {
            const p = schemas.api_cmd_api_organisation_ListSidersPathparamsSchema;
            const q = schemas.api_pkg_organisation_dto_ListSidersReqSchema;
            const res = schemas.api_pkg_organisation_dto_ListSidersRespSchema;
            type P = z.infer<typeof schemas.api_cmd_api_organisation_ListSidersPathparamsSchema>;
            type Q = z.infer<typeof schemas.api_pkg_organisation_dto_ListSidersReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_organisation_dto_ListSidersRespSchema>;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/siders" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                q,
                res,
            };
        },
    },
    "/tasks/{id}/siders/prospects": {
        GET: function () {
            const p = schemas.api_cmd_api_organisation_ListProspectSidersPathparamsSchema;
            const res = schemas.api_pkg_organisation_dto_ListProspectSidersRespSchema;
            type P = z.infer<
                typeof schemas.api_cmd_api_organisation_ListProspectSidersPathparamsSchema
            >;
            type Res = z.infer<
                typeof schemas.api_pkg_organisation_dto_ListProspectSidersRespSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/tasks/{id}/siders/prospects" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
    },
    "/upload/{type}/{id}": {
        POST: function () {
            const p = schemas.gitlab_side_co_pkg_sides3proxy_UploadHandlerPathparamsSchema;
            type P = z.infer<
                typeof schemas.gitlab_side_co_pkg_sides3proxy_UploadHandlerPathparamsSchema
            >;
            const types = {
                P: undefined as unknown as P,
            };
            const pathTemplate = "/upload/{type}/{id}" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                p,
            };
        },
    },
    "/users/search": {
        GET: function () {
            const q = schemas.api_cmd_api_search_LogasQuerySchema;
            const res = schemas.Services_team_api_dto_SearchUsersRespSchema;
            type Q = z.infer<typeof schemas.api_cmd_api_search_LogasQuerySchema>;
            type Res = z.infer<typeof schemas.Services_team_api_dto_SearchUsersRespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/users/search" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["authenticated", "logas"] as const,
                q,
                res,
            };
        },
    },
    "/v1/attendances/to_validate/count": {
        GET: function () {
            const res =
                schemas.team_api_controllers_attendances_GetAttendancesToValidateCountResponseSchema;
            type Res = z.infer<
                typeof schemas.team_api_controllers_attendances_GetAttendancesToValidateCountResponseSchema
            >;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/attendances/to_validate/count" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                res,
            };
        },
    },
    "/v1/attendances/validate": {
        POST: function () {
            const req = schemas.api_pkg_attendance_dto_ValidateAttendancesReqSchema;
            const res = schemas.api_pkg_attendance_dto_ValidateAttendancesRespSchema;
            type Req = z.infer<typeof schemas.api_pkg_attendance_dto_ValidateAttendancesReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_attendance_dto_ValidateAttendancesRespSchema>;
            const types = {
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/attendances/validate" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                req,
                res,
            };
        },
    },
    "/v1/attendances/{attendanceID}/dispute/{disputeID}": {
        GET: function () {
            const p = schemas.team_api_controllers_attendances_GetAttendanceDisputePathparamsSchema;
            const res = schemas.team_api_src_types_DisputeSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_attendances_GetAttendanceDisputePathparamsSchema
            >;
            type Res = z.infer<typeof schemas.team_api_src_types_DisputeSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/attendances/{attendanceID}/dispute/{disputeID}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
        PUT: function () {
            const p = schemas.team_api_controllers_attendances_PutAttendanceDisputePathparamsSchema;
            const req = schemas.team_api_src_types_PutDisputeBodySchema;
            const res = schemas.team_api_src_types_DisputeSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_attendances_PutAttendanceDisputePathparamsSchema
            >;
            type Req = z.infer<typeof schemas.team_api_src_types_PutDisputeBodySchema>;
            type Res = z.infer<typeof schemas.team_api_src_types_DisputeSchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/attendances/{attendanceID}/dispute/{disputeID}" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/v1/attendances/{attendanceID}/review": {
        POST: function () {
            const p = schemas.team_api_controllers_attendances_PostAttendanceReviewPathparamsSchema;
            const req = schemas.team_api_src_types_AttendanceReviewBodySchema;
            const res = schemas.team_api_src_types_AttendanceReviewBodySchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_attendances_PostAttendanceReviewPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.team_api_src_types_AttendanceReviewBodySchema>;
            type Res = z.infer<typeof schemas.team_api_src_types_AttendanceReviewBodySchema>;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/attendances/{attendanceID}/review" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/v1/feedback/organisation/{organisationId}/sider/{siderId}": {
        POST: function () {
            const p = schemas.team_api_controllers_feedback_PostFeedbackPathparamsSchema;
            const req = schemas.team_api_src_types_FeedbackBodySchema;
            const res = schemas.team_api_controllers_feedback_PostFeedbackResponseSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_feedback_PostFeedbackPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.team_api_src_types_FeedbackBodySchema>;
            type Res = z.infer<
                typeof schemas.team_api_controllers_feedback_PostFeedbackResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate =
                "/v1/feedback/organisation/{organisationId}/sider/{siderId}" as const;
            const method = "POST" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/v1/feedback/{feedbackId}": {
        GET: function () {
            const p = schemas.team_api_controllers_feedback_GetFeedbackPathparamsSchema;
            const res = schemas.team_api_src_types_FeedbackBodyUpdateSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_feedback_GetFeedbackPathparamsSchema
            >;
            type Res = z.infer<typeof schemas.team_api_src_types_FeedbackBodyUpdateSchema>;
            const types = {
                P: undefined as unknown as P,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/feedback/{feedbackId}" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                res,
            };
        },
        PUT: function () {
            const p = schemas.team_api_controllers_feedback_UpdateFeedbackPathparamsSchema;
            const req = schemas.team_api_src_types_FeedbackBodyUpdateSchema;
            const res = schemas.team_api_controllers_feedback_UpdateFeedbackResponseSchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_feedback_UpdateFeedbackPathparamsSchema
            >;
            type Req = z.infer<typeof schemas.team_api_src_types_FeedbackBodyUpdateSchema>;
            type Res = z.infer<
                typeof schemas.team_api_controllers_feedback_UpdateFeedbackResponseSchema
            >;
            const types = {
                P: undefined as unknown as P,
                Req: undefined as unknown as Req,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/feedback/{feedbackId}" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                p,
                req,
                res,
            };
        },
    },
    "/v1/organisations": {
        GET: function () {
            const res = schemas.api_pkg_organisation_dto_GetOrganisationRespSchema;
            type Res = z.infer<typeof schemas.api_pkg_organisation_dto_GetOrganisationRespSchema>;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/organisations" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                res,
            };
        },
    },
    "/v1/organisations/invoices": {
        GET: function () {
            const q = schemas.team_api_controllers_invoice_GetOrganisationInvoicesQuerySchema;
            const res = schemas.team_api_controllers_invoice_GetOrganisationInvoicesResponseSchema;
            type Q = z.infer<
                typeof schemas.team_api_controllers_invoice_GetOrganisationInvoicesQuerySchema
            >;
            type Res = z.infer<
                typeof schemas.team_api_controllers_invoice_GetOrganisationInvoicesResponseSchema
            >;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/organisations/invoices" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                q,
                res,
            };
        },
    },
    "/v1/organisations/invoices/download": {
        GET: function () {
            const q = schemas.team_api_controllers_organisations_GetOrganisationInvoicesQuerySchema;
            const res = schemas.team_api_src_types_InvoiceListSchema;
            type Q = z.infer<
                typeof schemas.team_api_controllers_organisations_GetOrganisationInvoicesQuerySchema
            >;
            type Res = z.infer<typeof schemas.team_api_src_types_InvoiceListSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/organisations/invoices/download" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                q,
                res,
            };
        },
    },
    "/v1/planning": {
        GET: function () {
            const q = schemas.Services_team_api_dto_GetPlanningReqSchema;
            const res = schemas.Services_team_api_dto_PlanningSchema;
            type Q = z.infer<typeof schemas.Services_team_api_dto_GetPlanningReqSchema>;
            type Res = z.infer<typeof schemas.Services_team_api_dto_PlanningSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/planning" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                q,
                res,
            };
        },
    },
    "/v1/users/me": {
        GET: function () {
            const res = schemas.team_api_src_types_UserMeSchema;
            type Res = z.infer<typeof schemas.team_api_src_types_UserMeSchema>;
            const types = {
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v1/users/me" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["authenticated"] as const,
                res,
            };
        },
        PUT: function () {
            const req = schemas.team_api_src_types_UserUpdateBodySchema;
            type Req = z.infer<typeof schemas.team_api_src_types_UserUpdateBodySchema>;
            const types = {
                Req: undefined as unknown as Req,
            };
            const pathTemplate = "/v1/users/me" as const;
            const method = "PUT" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "enabled" as const,
                    generateKey: true,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["authenticated"] as const,
                req,
            };
        },
    },
    "/v1/{service}/download": {
        GET: function () {
            const p = schemas.team_api_controllers_invoice_DownloadInvoicesPathparamsSchema;
            const q = schemas.team_api_controllers_invoice_DownloadInvoicesQuerySchema;
            type P = z.infer<
                typeof schemas.team_api_controllers_invoice_DownloadInvoicesPathparamsSchema
            >;
            type Q = z.infer<
                typeof schemas.team_api_controllers_invoice_DownloadInvoicesQuerySchema
            >;
            const types = {
                P: undefined as unknown as P,
                Q: undefined as unknown as Q,
            };
            const pathTemplate = "/v1/{service}/download" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["account"] as const,
                p,
                q,
            };
        },
    },
    "/v2/attendances": {
        GET: function () {
            const q = schemas.api_pkg_attendance_dto_ListFormattedAttendancesParamsSchema;
            const res = schemas.api_pkg_attendance_dto_ListFormattedAttendancesRespSchema;
            type Q = z.infer<
                typeof schemas.api_pkg_attendance_dto_ListFormattedAttendancesParamsSchema
            >;
            type Res = z.infer<
                typeof schemas.api_pkg_attendance_dto_ListFormattedAttendancesRespSchema
            >;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v2/attendances" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                q,
                res,
            };
        },
    },
    "/v2/tasks": {
        GET: function () {
            const q = schemas.api_pkg_task_dto_ListTasksV2ReqSchema;
            const res = schemas.api_pkg_task_dto_ListTasksV2RespSchema;
            type Q = z.infer<typeof schemas.api_pkg_task_dto_ListTasksV2ReqSchema>;
            type Res = z.infer<typeof schemas.api_pkg_task_dto_ListTasksV2RespSchema>;
            const types = {
                Q: undefined as unknown as Q,
                Res: undefined as unknown as Res,
            };
            const pathTemplate = "/v2/tasks" as const;
            const method = "GET" as const;

            return {
                types,
                pathTemplate,
                method,
                idempotency: {
                    mode: "disabled" as const,
                    generateKey: false,
                    required: false,
                    requireIdentity: false,
                },
                requiredScopes: ["accountOrNetwork"] as const,
                q,
                res,
            };
        },
    },
} as const;

export function spellbook<Path extends keyof typeof endpoints>(path: Path) {
    return endpoints[path];
}
